import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Guide {
  id: string;
  title: string;
  summary: string;
  tag: string;
  read_time: string;
  author: string;
  image: string;
  created_at: string;
}

const DISPLAY_COUNT = 5;

function pickRandom<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
}

export default function BuyingGuides() {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.from('buying_guides').select('*').then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        setError(true);
      } else if (data) {
        setGuides(pickRandom(data, DISPLAY_COUNT));
      }
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setError(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="py-20 md:py-28 bg-ink-50/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center text-ink-400 text-sm">Loading guides...</div>
      </section>
    );
  }

  if (error || guides.length === 0) return null;

  const [first, ...rest] = guides;
  if (!first) return null;

  return (
    <section id="guides" className="py-20 md:py-28 bg-ink-50/60 reveal">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-cta-600">Buying Guides</span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-ink-900 tracking-tight">
              Read before you buy
            </h2>
            <p className="mt-3 text-ink-600 leading-relaxed">
              Long-form guides written by category specialists, updated monthly with fresh recommendations.
            </p>
          </div>
          <Link to="/guides" className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-ink-800 hover:text-cta-600 transition whitespace-nowrap">
            All buying guides
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-base"></i>
            </span>
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Feature guide */}
          <Link to={`/guides/${first.id}`} className="group relative overflow-hidden rounded-2xl bg-white border border-ink-100 shadow-soft block h-full">
            <div className="relative h-full min-h-[320px] overflow-hidden">
              <img src={first.image} alt={first.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-ink-900/10 to-transparent"></div>
              <span className="absolute top-4 left-4 h-7 px-3 rounded-full bg-white text-ink-900 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1">
                <i className="ri-fire-line text-cta-500"></i> Featured
              </span>
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <span className="inline-flex items-center h-6 px-2 rounded-md bg-cta-600 text-[10px] font-bold uppercase tracking-wider">
                  {first.tag}
                </span>
                <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold leading-tight">{first.title}</h3>
                <p className="mt-2 text-sm text-white/80 line-clamp-2 max-w-lg">{first.summary}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-white/70">
                  <span>{first.author}</span>
                  <span>&middot;</span>
                  <span>{new Date(first.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  <span>&middot;</span>
                  <span>{first.read_time}</span>
                </div>
              </div>
            </div>
          </Link>

          {/* List of guides */}
          <div className="space-y-4">
            {rest.map((g) => (
                <Link key={g.id} to={`/guides/${g.id}`} className="group flex gap-4 p-3 rounded-2xl bg-white border border-ink-100 hover:border-cta-200 hover:shadow-card transition">
                  <div className="w-32 md:w-44 aspect-[4/3] rounded-xl bg-ink-50 overflow-hidden flex-shrink-0">
                    <img src={g.image} alt={g.title} className="w-full h-full object-contain p-2 group-hover:scale-105 transition duration-500" />
                  </div>
                  <div className="flex-1 min-w-0 py-1 pr-2">
                    <span className="inline-flex items-center h-5 px-1.5 rounded bg-cta-50 text-cta-700 text-[10px] font-bold uppercase tracking-wider">
                      {g.tag}
                    </span>
                    <h3 className="mt-2 font-semibold text-ink-900 leading-snug line-clamp-2">{g.title}</h3>
                    <p className="mt-1 text-xs text-ink-500 line-clamp-2 leading-relaxed">{g.summary}</p>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-400">
                      <span>{g.author}</span>
                      <span>&middot;</span>
                      <span>{g.read_time}</span>
                      <span className="ml-auto text-cta-600 font-semibold flex items-center gap-1 whitespace-nowrap">
                        Read More
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-base"></i>
                        </span>
                      </span>
                    </div>
                  </div>
                </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}